import { useEffect } from "react";
import { useNavigate } from "react-router";
import { useServices } from "@/app/use-services";
import { useMomentOnce } from "@/app/use-moment";
import { Working } from "@/ui/Working";
import { useDraft } from "../draft-store";
import { setConsented, toBirthData } from "../draft";
import { firstUnansweredStep, PATHS } from "../destinations";
import { Consent } from "../screens/Consent";
import { WeekReveal } from "../screens/WeekReveal";
import { TalkInvitation } from "../screens/TalkInvitation";
import { useOnboardingShared } from "../use-onboarding-shared";
import { useAsyncAction } from "../use-async-action";

const WORKING_MESSAGE = "Reading your week…";

/**
 * The reveal: their week. On a reload the week is asked for again, and the
 * server answers with the same week for the same chart.
 */
export function RevealRoute() {
  const { api, draft: store, analytics } = useServices();
  const draft = useDraft(store);
  const navigate = useNavigate();
  const shared = useOnboardingShared();
  const recompute = useAsyncAction(WORKING_MESSAGE);
  useMomentOnce(shared.week ? { name: "chart revealed" } : null);

  useEffect(() => {
    if (shared.week) return;
    const missing = firstUnansweredStep(draft);
    if (missing) {
      navigate(missing, { replace: true });
      return;
    }
    const birth = toBirthData(draft);
    if (!birth) return;
    void recompute.run(async () => shared.setWeek(await api.previewWeek(birth))).then((ok) => {
      if (!ok) navigate(PATHS.place, { replace: true });
    });
    // Runs once per mount: the deps that matter are stable stores.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!shared.week) return <Working message={WORKING_MESSAGE} />;
  return (
    <WeekReveal
      week={shared.week}
      onContinue={() => {
        analytics.track({ name: "reveal continued" });
        navigate(PATHS.consent);
      }}
    />
  );
}

/**
 * Consent is remembered in the draft and recorded server-side once there is
 * an account to record it against — the person is not signed in yet here.
 */
export function ConsentRoute() {
  const { draft: store, analytics } = useServices();
  const navigate = useNavigate();
  return (
    <Consent
      onAgree={() => {
        analytics.track({ name: "consent given" });
        store.update((d) => setConsented(d, true));
        navigate(PATHS.signIn);
      }}
    />
  );
}

/**
 * The last screen. Both routes save the chart; they differ in where they land.
 * Tapping to speak lands in the call itself, which connects on arrival.
 */
export function TalkRoute() {
  const navigate = useNavigate();
  const { analytics } = useServices();
  const go = (then: string, choice: "talk" | "later") => {
    analytics.track({ name: "talk invitation answered", choice });
    navigate(`${PATHS.saving}?then=${encodeURIComponent(then)}`);
  };
  return <TalkInvitation onTry={() => go(PATHS.voiceCall, "talk")} onLater={() => go(PATHS.today, "later")} />;
}
